import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from '../../actions/user.actions';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import LoginForm from './components/LoginForm/LoginForm';
import './container.scss';
import { OTPForm } from './components/OTPForm/OTPForm';
import { OTPMethodSelectForm } from './components/OTPMethodSelectForm/OTPMethodSelectForm';
import { MaxPhoneForm } from './components/MaxPhoneForm/MaxPhoneForm';
import { getMaxBindingStatus } from './requests/getMaxBindingStatus';

const SUPPORT_CONTACT_NOTICE_TEXT =
  'Все доступные способы подтверждения недоступны. Пожалуйста, обратитесь в техническую поддержку.';

const FREE_VERIFICATION_TYPE_IDS = [3, 4, 5];
const PAID_VERIFICATION_TYPE_IDS = [1, 2];
const MAX_FAILED_OTP_ATTEMPTS = 3;
const MAX_VERIFICATION_TYPE_ID = 5;
const MAX_BINDING_POLL_INTERVAL_MS = 5000;
const MAX_BINDING_POLL_LIMIT = 5;

const getDeepLink = (response) => response?.deepLink || response?.DeepLink;

const getNeedsPhone = (response) =>
  response?.needsPhone || response?.NeedsPhone;

const getIsMaxBound = (response) => {
  const value = response?.isBound ?? response?.IsBound;
  return value === true || value === 'true' || value === 1;
};

const requestMaxBindingStatus = async ({ login, password }) => {
  const bindingStatusResponse = await getMaxBindingStatus({
    login,
    password,
  });
  return getIsMaxBound(bindingStatusResponse);
};

const Login = () => {
  const dispatch = useDispatch();
  const [forgotPasswordModalOpened, setForgotPasswordModalOpened] =
    useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [OTPType, setOTPType] = useState(null);
  const [maxDeepLink, setMaxDeepLink] = useState(null);
  const [isMaxBound, setIsMaxBound] = useState(null);
  const isLoginMountedRef = useRef(true);
  const maxBindingPollIntervalRef = useRef(null);
  const maxBindingPollCountRef = useRef(0);
  const maxBindingPollInFlightRef = useRef(false);
  const [savedVerificationTypes, setSavedVerificationTypes] = useState(null);
  const [failedOtpAttemptsByTypeId, setFailedOtpAttemptsByTypeId] = useState(
    {}
  );

  const getFailedAttemptsForTypeId = (typeId) => {
    const key = String(typeId);
    return failedOtpAttemptsByTypeId?.[key] || 0;
  };

  const verificationTypesFromStore = useSelector(
    (state) => state.authentication?.twoFactorVerificationTypes
  );

  useEffect(() => {
    if (
      Array.isArray(verificationTypesFromStore) &&
      verificationTypesFromStore.length > 0
    ) {
      if (
        !savedVerificationTypes ||
        savedVerificationTypes.length === 0 ||
        verificationTypesFromStore.length !== savedVerificationTypes.length
      ) {
        setSavedVerificationTypes(verificationTypesFromStore);
        setFailedOtpAttemptsByTypeId({});
      }
    }
  }, [verificationTypesFromStore, savedVerificationTypes]);

  useEffect(() => {
    dispatch(userActions.logout());
  }, [dispatch]);

  const clearMaxBindingPolling = useCallback(() => {
    if (maxBindingPollIntervalRef.current) {
      clearInterval(maxBindingPollIntervalRef.current);
      maxBindingPollIntervalRef.current = null;
    }

    maxBindingPollCountRef.current = 0;
    maxBindingPollInFlightRef.current = false;
  }, []);

  const handleChangeOTPType = (verificationType) => {
    setOTPType(verificationType);
    setMaxDeepLink(null);
    setIsMaxBound(null);
    clearMaxBindingPolling();
  };

  const refreshMaxBindingStatus = useCallback(async () => {
    const nextIsMaxBound = await requestMaxBindingStatus({
      login: username,
      password,
    });

    if (!isLoginMountedRef.current) {
      return false;
    }

    setIsMaxBound(nextIsMaxBound);

    if (nextIsMaxBound) {
      clearMaxBindingPolling();
    }

    return nextIsMaxBound;
  }, [clearMaxBindingPolling, username, password]);

  const startMaxBindingPolling = useCallback(() => {
    clearMaxBindingPolling();

    const checkBindingStatus = async () => {
      if (
        maxBindingPollInFlightRef.current ||
        maxBindingPollCountRef.current >= MAX_BINDING_POLL_LIMIT
      ) {
        return;
      }

      maxBindingPollInFlightRef.current = true;
      maxBindingPollCountRef.current += 1;
      const nextIsMaxBound = await refreshMaxBindingStatus();
      maxBindingPollInFlightRef.current = false;

      if (
        nextIsMaxBound ||
        maxBindingPollCountRef.current >= MAX_BINDING_POLL_LIMIT
      ) {
        clearMaxBindingPolling();
      }
    };

    checkBindingStatus();

    maxBindingPollIntervalRef.current = setInterval(
      checkBindingStatus,
      MAX_BINDING_POLL_INTERVAL_MS
    );
  }, [clearMaxBindingPolling, refreshMaxBindingStatus]);

  useEffect(() => {
    return () => {
      isLoginMountedRef.current = false;
      clearMaxBindingPolling();
    };
  }, [clearMaxBindingPolling]);

  const handleMaxLinkClick = useCallback(() => {
    startMaxBindingPolling();
  }, [startMaxBindingPolling]);

  const handleVerificationCodeResponse = async (
    verificationType,
    verificationCodeResponse
  ) => {
    const verificationTypeId = Number(verificationType?.id);

    if (verificationTypeId !== MAX_VERIFICATION_TYPE_ID) {
      return false;
    }

    setMaxDeepLink(getDeepLink(verificationCodeResponse) || null);
    setIsMaxBound(null);

    const nextIsMaxBound = await requestMaxBindingStatus({
      login: username,
      password,
    });
    setIsMaxBound(nextIsMaxBound);

    if (getNeedsPhone(verificationCodeResponse)) {
      setStep(4);
      return true;
    }

    setStep(3);
    return true;
  };

  const handleMaxPhoneVerificationCodeResponse = async (
    verificationCodeResponse
  ) => {
    await handleVerificationCodeResponse(
      { id: MAX_VERIFICATION_TYPE_ID },
      verificationCodeResponse
    );
  };

  const handleOtpFailedAttempt = (verificationType) => {
    if (!verificationType) return;

    const typeId = Number(verificationType?.id);
    if (!Number.isFinite(typeId)) return;

    setFailedOtpAttemptsByTypeId((prev) => {
      const key = String(typeId);
      return { ...prev, [key]: (prev[key] || 0) + 1 };
    });
  };

  const [step, setStep] = useState(1);

  useEffect(() => {
    if (step !== 3 || Number(OTPType?.id) !== MAX_VERIFICATION_TYPE_ID) {
      clearMaxBindingPolling();
    }
  }, [OTPType, clearMaxBindingPolling, step]);

  const failedFreeOtpAttempts = FREE_VERIFICATION_TYPE_IDS.reduce(
    (sum, typeId) => sum + getFailedAttemptsForTypeId(typeId),
    0
  );
  const shouldShowPaidVerificationTypes =
    failedFreeOtpAttempts >= MAX_FAILED_OTP_ATTEMPTS;

  const verificationTypesForSelection = useMemo(() => {
    const baseVerificationTypes = Array.isArray(savedVerificationTypes)
      ? savedVerificationTypes
      : verificationTypesFromStore;

    if (!Array.isArray(baseVerificationTypes)) return baseVerificationTypes;

    const getSortIndex = (type) => {
      const typeId = Number(type?.id);
      const freeIndex = FREE_VERIFICATION_TYPE_IDS.indexOf(typeId);
      if (freeIndex !== -1) return freeIndex;

      const paidIndex = PAID_VERIFICATION_TYPE_IDS.indexOf(typeId);
      if (paidIndex !== -1) {
        return FREE_VERIFICATION_TYPE_IDS.length + paidIndex;
      }

      return (
        FREE_VERIFICATION_TYPE_IDS.length + PAID_VERIFICATION_TYPE_IDS.length
      );
    };

    return baseVerificationTypes
      .filter((type) => {
        const typeId = Number(type?.id);
        if (!Number.isFinite(typeId)) return false;

        if (FREE_VERIFICATION_TYPE_IDS.includes(typeId)) return true;

        if (PAID_VERIFICATION_TYPE_IDS.includes(typeId)) {
          return shouldShowPaidVerificationTypes;
        }

        return false;
      })
      .sort((first, second) => getSortIndex(first) - getSortIndex(second));
  }, [
    savedVerificationTypes,
    shouldShowPaidVerificationTypes,
    verificationTypesFromStore,
  ]);

  const failedAttemptsForSelectedOTPType = OTPType
    ? getFailedAttemptsForTypeId(OTPType?.id)
    : 0;

  const disabledVerificationTypesById = useMemo(() => {
    const getMessage = () =>
      'Метод временно заблокирован после 3 неверных попыток. Пожалуйста, выберите другой метод.';

    const result = {};
    [...FREE_VERIFICATION_TYPE_IDS, ...PAID_VERIFICATION_TYPE_IDS].forEach(
      (typeId) => {
        const failedAttempts = failedOtpAttemptsByTypeId?.[String(typeId)] || 0;

        if (failedAttempts >= MAX_FAILED_OTP_ATTEMPTS) {
          result[String(typeId)] = getMessage();
        }
      }
    );

    return result;
  }, [failedOtpAttemptsByTypeId]);

  const allVerificationTypesBlocked = useMemo(() => {
    if (
      !Array.isArray(verificationTypesForSelection) ||
      verificationTypesForSelection.length === 0
    ) {
      return false;
    }
    return verificationTypesForSelection.every((type) =>
      Boolean(disabledVerificationTypesById[String(type?.id)])
    );
  }, [verificationTypesForSelection, disabledVerificationTypesById]);

  const supportContactNotice = allVerificationTypesBlocked
    ? SUPPORT_CONTACT_NOTICE_TEXT
    : null;

  return (
    <>
      {forgotPasswordModalOpened && (
        <ForgotPassword
          defaultUsername={username}
          setForgotPasswordModalOpened={setForgotPasswordModalOpened}
        />
      )}
      <div className="login_page">
        <div className="login_page-main">
          <div className="login_page-main-container">
            <div className="logo-container">
              <img
                alt="dispex logo"
                src="/img/secondary_logo_full.png"
                className="logo"
              />
              {step === 2 && (
                <img
                  className="id"
                  src="/img/id.svg"
                  alt="id"
                />
              )}
            </div>
            {step === 1 && (
              <LoginForm
                setStep={setStep}
                username={username}
                setUsername={setUsername}
                password={password}
                setPassword={setPassword}
                setForgotPasswordModalOpened={setForgotPasswordModalOpened}
              />
            )}
            {step === 2 && (
              <OTPMethodSelectForm
                setStep={setStep}
                onMethodSelect={handleChangeOTPType}
                username={username}
                password={password}
                verificationTypes={verificationTypesForSelection}
                showAllMethods={true}
                disabledVerificationTypesById={disabledVerificationTypesById}
                supportContactNotice={supportContactNotice}
                onVerificationCodeResponse={handleVerificationCodeResponse}
              />
            )}
            {step === 3 && OTPType && (
              <OTPForm
                key={OTPType.id}
                setStep={setStep}
                verificationType={OTPType}
                username={username}
                password={password}
                onFailedAttempt={handleOtpFailedAttempt}
                failedAttempts={failedAttemptsForSelectedOTPType}
                trackAttempts={true}
                supportContactNotice={supportContactNotice}
                deepLink={
                  Number(OTPType?.id) === MAX_VERIFICATION_TYPE_ID
                    ? maxDeepLink
                    : null
                }
                isMaxBound={
                  Number(OTPType?.id) === MAX_VERIFICATION_TYPE_ID
                    ? isMaxBound
                    : null
                }
                onVerificationCodeResponse={handleVerificationCodeResponse}
                onMaxLinkClick={handleMaxLinkClick}
              />
            )}
            {step === 4 && (
              <MaxPhoneForm
                setStep={setStep}
                username={username}
                password={password}
                onVerificationCodeResponse={
                  handleMaxPhoneVerificationCodeResponse
                }
              />
            )}
          </div>
        </div>
        <div className="footer">
          <div className="footer_text">
            <span>© 2026 Dispex. Все права защищены</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
